/**
 * useChangeControl — react-query hooks for the new change_control module
 * (GET /api/v1/change-control/requests). Distinct from the legacy cc_impact
 * engine at /changes.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '../../api/client';

export interface CrSummary {
  cr_id: string;
  cr_number: string | null;
  title: string;
  status: string;
  gmp_classification: string | null;
  change_level: string | null;
  change_type: string | null;
  department: string | null;
  initiator_name: string | null;
  duration: string | null;
  migration_label: string | null;
  created_at_utc: string;
  updated_at_utc: string;
}
export interface CcStats { total: number; open: number; closed: number; critical: number; pending_approval: number }
export interface CcListResp { items: CrSummary[]; stats: CcStats }

// CC-14 — Change-Control dashboard / KPIs.
export interface CcDashboard {
  totals: { total: number; open: number; closed: number; overdue: number; gmp: number; non_gmp: number; implemented_before_approval: number; avg_closure_days: number };
  by_status: Array<{ status: string; n: number }>;
  by_type: Array<{ change_type: string; n: number }>;
  by_department: Array<{ department: string; n: number }>;
  by_risk: Array<{ gmp: string; n: number }>;
  top_overdue: Array<{ cr_id: string; cr_number: string | null; title: string; open_overdue: number; days_overdue: number }>;
  trend: Array<{ ym: string; created: number; closed: number }>;
  insights: Array<{ kind: string; key: string; params: Record<string, unknown> }>;
}
export function useCcDashboard() {
  return useQuery<CcDashboard>({
    queryKey: ['change-control', 'dashboard'],
    queryFn: ({ signal }) => apiFetch('/api/v1/change-control/dashboard', { signal }),
    staleTime: 60_000,
  });
}

export function useChangeRequests(params: { status?: string } = {}) {
  return useQuery<CcListResp>({
    queryKey: ['change-control', 'requests', params],
    queryFn: ({ signal }) =>
      apiFetch<CcListResp>(`/api/v1/change-control/requests${params.status ? `?status=${encodeURIComponent(params.status)}` : ''}`, { signal }),
    staleTime: 20_000,
  });
}

export interface CrHistoryRow {
  history_id: string; event_type: string; from_status: string | null; to_status: string | null;
  actor_name: string | null; reason: string | null; event_at_utc: string;
}
// Row shapes for the CrDetail sub-collections. Fields mirror the
// change-control API payload; values are display-only in the detail screen
// (rendered via stringify/Number helpers), hence the permissive null unions.
export interface CrImpactRow {
  impact_id: string | number;
  department: string | null;
  has_impact: boolean | null;
  assessment_text: string | null;
  target_date: string | null;
  owner_name: string | null;
}
export interface CrQraRow {
  qra_id: string | number;
  qra_number: string | null;
  subject: string | null;
  severity: number | null;
  occurrence: number | null;
  detectability: number | null;
  rpn: number | null;
  risk_category: string | null;
  residual_rpn: number | null;
  decision: string | null;
}
export interface CrActionRow {
  action_id: string;
  seq: number | null;
  description: string | null;
  category: string | null;
  owner_name: string | null;
  target_date: string | null;
  status: string | null;
}
export interface CrApprovalRow {
  approval_id: string | number;
  stage: string | null;
  decision: string | null;
  approver_name: string | null;
  decided_at: string | null;
  comments: string | null;
}
export interface CrEffRow {
  eff_id: string | number;
  review_due_date: string | null;
  outcome: string | null;
  objective_achieved: boolean | null;
  adverse_impact: boolean | null;
  capa_ref: string | null;
}
export interface CrEvidenceRow {
  evidence_id: string;
  category: string;
  title: string | null;
  filename: string | null;
  mime: string | null;
  notes: string | null;
  linked_action_id: string | null;
  uploaded_at_utc: string;
  has_file: boolean | null;
}
export interface CrSubAssessmentRow {
  sub_id: string;
  kind: string;                 // REGULATORY / VALIDATION / TRAINING
  title: string;
  required: boolean | null;
  status: string;
  variation_type: string | null;
  country: string | null;
  authority: string | null;
  submission_before_impl: boolean | null;
  validation_type: string | null;
  protocol_ref: string | null;
  report_ref: string | null;
  audience: string | null;
  training_type: string | null;
  document_ref: string | null;
  reference_no: string | null;
  target_date: string | null;
  owner_name: string | null;
  notes: string | null;
  auto_generated: boolean | null;
  linked_action_id: string | null;
  created_at_utc: string;
  updated_at_utc: string;
}
export interface CrClosureGate { key: string; passed: boolean; open: number; total: number }
export interface CrClosureGates {
  gates: CrClosureGate[];
  passed: number;
  total: number;
  ready_pct: number;
  all_passed: boolean;
}
export interface CrAuditRow {
  audit_id: string;
  event_kind: string;
  target_entity_type: string | null;
  target_entity_id: string | null;
  recorded_at_utc: string;
  actor_user_id: string | null;
  payload: Record<string, unknown> | null;
}
// CC-15 — Part-11 audit trail (lazy: only fetched when the panel is opened).
export function useAuditTrail(id: string, enabled: boolean) {
  return useQuery<{ items: CrAuditRow[] }>({
    queryKey: ['change-control', 'audit-trail', id],
    queryFn: ({ signal }) => apiFetch(`/api/v1/change-control/requests/${id}/audit-trail`, { signal }),
    enabled: !!id && enabled,
    staleTime: 30_000,
  });
}

// CC-10 — committee / assessment-team.
export interface CrCommitteeRow {
  member_id: string;
  member_name: string;
  member_role: string;
  vote: string;
  comments: string | null;
  voted_at_utc: string | null;
  created_at_utc: string;
}
export interface CrCommitteeSummary {
  total: number; voted: number; pending: number;
  tally: Record<string, number>; quorum_met: boolean; decision: string;
}
export const CC_COMMITTEE_ROLES = ['CHAIR', 'SECRETARY', 'MEMBER', 'SME'];
export const CC_COMMITTEE_VOTES = ['PENDING', 'APPROVE', 'APPROVE_WITH_CONDITIONS', 'REJECT', 'ABSTAIN'];
export interface NewCommitteeInput { member_name: string; member_role?: string }
export function useAddCommittee(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewCommitteeInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/committee`, { method: 'POST', body }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function usePatchCommittee(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, { memberId: string; patch: Record<string, unknown> }>({
    mutationFn: ({ memberId, patch }) => apiFetch(`/api/v1/change-control/requests/${id}/committee/${memberId}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useDeleteCommittee(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (memberId) => apiFetch(`/api/v1/change-control/requests/${id}/committee/${memberId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export interface CrDetail extends CrSummary {
  section: string | null;
  source_of_change: string[] | null;
  description: string | null;
  current_practice: string | null;
  proposed_change: string | null;
  reason: string | null;
  related_deviation_no: string | null;
  related_qra_no: string | null;
  quality_impact: string | null;
  safety_impact: string | null;
  implementable: boolean | null;
  effectiveness_required: boolean;
  effectiveness_due_days: number | null;
  qa_preliminary_at: string | null;
  final_approved_at: string | null;
  implemented_at: string | null;
  closed_at: string | null;
  impacts: CrImpactRow[];
  qra: CrQraRow[];
  actions: CrActionRow[];
  approvals: CrApprovalRow[];
  effectiveness: CrEffRow[];
  evidence: CrEvidenceRow[];
  sub_assessments: CrSubAssessmentRow[];
  closure_gates: CrClosureGates;
  committee: CrCommitteeRow[];
  committee_summary: CrCommitteeSummary;
  history: CrHistoryRow[];
}

export function useChangeRequest(id: string) {
  return useQuery<CrDetail>({
    queryKey: ['change-control', 'request', id],
    queryFn: ({ signal }) => apiFetch<CrDetail>(`/api/v1/change-control/requests/${id}`, { signal }),
    enabled: !!id,
  });
}

// 15-status lifecycle FSM (mirrors change_control CC_FSM in pgHandlers.mjs).
// Maps a status to the set of statuses it may transition into.
export const CC_FSM: Record<string, string[]> = {
  DRAFT: ['SUBMITTED', 'CANCELLED'],
  SUBMITTED: ['PENDING_QA_PRELIMINARY', 'DRAFT', 'CANCELLED'],
  PENDING_QA_PRELIMINARY: ['ACCEPTED_CR_NUMBER_ISSUED', 'REJECTED'],
  ACCEPTED_CR_NUMBER_ISSUED: ['IMPACT_ASSESSMENT', 'ON_HOLD', 'CANCELLED'],
  IMPACT_ASSESSMENT: ['RISK_ASSESSMENT', 'COMMITTEE_REVIEW', 'ON_HOLD'],
  RISK_ASSESSMENT: ['COMMITTEE_REVIEW', 'ON_HOLD'],
  COMMITTEE_REVIEW: ['PENDING_FINAL_APPROVAL', 'REJECTED', 'ON_HOLD'],
  PENDING_FINAL_APPROVAL: ['APPROVED_FOR_IMPLEMENTATION', 'REJECTED', 'ON_HOLD'],
  APPROVED_FOR_IMPLEMENTATION: ['IMPLEMENTATION_IN_PROGRESS', 'ON_HOLD'],
  IMPLEMENTATION_IN_PROGRESS: ['PENDING_EFFECTIVENESS', 'PENDING_CLOSURE', 'ON_HOLD'],
  PENDING_EFFECTIVENESS: ['PENDING_CLOSURE', 'ON_HOLD'],
  PENDING_CLOSURE: ['CLOSED_APPROVED', 'ON_HOLD'],
  CLOSED_APPROVED: ['IMPLEMENTATION_IN_PROGRESS'], REJECTED: [], CANCELLED: [],
  ON_HOLD: ['IMPACT_ASSESSMENT', 'RISK_ASSESSMENT', 'COMMITTEE_REVIEW', 'PENDING_FINAL_APPROVAL', 'IMPLEMENTATION_IN_PROGRESS', 'CANCELLED'],
};
// Transitions that must carry a reason (stored as cancelled_reason / on_hold_reason / rejection rationale).
export const CC_REASON_REQUIRED = new Set(['CANCELLED', 'ON_HOLD', 'REJECTED']);

export interface NewCrInput {
  title: string;
  change_type?: string;
  department?: string;
  section?: string;
  source_of_change?: string[];
  duration?: string;
  gmp_classification?: string;
  change_level?: string;
  description?: string;
  current_practice?: string;
  proposed_change?: string;
  reason?: string;
}

export interface ChangeTypeRule {
  change_type: string;
  label: string;
  default_gmp: string | null;
  default_level: string | null;
  required_departments: string[];
  required_assessments: string[];
}
export function useChangeTypes() {
  return useQuery<{ items: ChangeTypeRule[] }>({
    queryKey: ['change-control', 'change-types'],
    queryFn: ({ signal }) => apiFetch('/api/v1/change-control/change-types', { signal }),
    staleTime: 5 * 60_000,
  });
}

export function useCreateCr() {
  const qc = useQueryClient();
  return useMutation<CrDetail, Error, NewCrInput>({
    mutationFn: (body) => apiFetch<CrDetail>('/api/v1/change-control/requests', { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'requests'] }),
  });
}

export function useTransitionCr(id: string) {
  const qc = useQueryClient();
  return useMutation<CrDetail, Error, { to: string; reason?: string }>({
    mutationFn: ({ to, reason }) =>
      apiFetch<CrDetail>(`/api/v1/change-control/requests/${id}/transition`, { method: 'POST', body: { to_status: to, reason } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['change-control', 'request', id] });
      qc.invalidateQueries({ queryKey: ['change-control', 'requests'] });
    },
  });
}

// Part B — per-department impact assessment.
export const CC_DEPARTMENTS = ['VALIDATION', 'ENGINEERING', 'QC', 'PRODUCTION', 'PACKAGING', 'SUPPLY_CHAIN', 'WAREHOUSE', 'RA', 'QA', 'RND', 'EHS', 'OTHER'];
export interface NewImpactInput { department: string; has_impact?: boolean | null; assessment_text?: string; target_date?: string; owner_name?: string }

export function useAddImpact(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewImpactInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/impacts`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}
export function useDeleteImpact(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (impactId) => apiFetch(`/api/v1/change-control/requests/${id}/impacts/${impactId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}

// QRA / FMEA (Model A: S/O/D 1-5, RPN = S*O*D, LOW<28 / MODERATE 28-74 / HIGH>=75).
export const CC_QRA_DECISIONS = ['ACCEPTED', 'ACCEPTED_WITH_MITIGATION', 'NOT_ACCEPTED'];
export function rpnCategory(rpn: number): 'LOW' | 'MODERATE' | 'HIGH' {
  return rpn < 28 ? 'LOW' : rpn < 75 ? 'MODERATE' : 'HIGH';
}
export interface NewQraInput {
  subject?: string; severity: number; occurrence: number; detectability: number; mitigation?: string;
  residual_severity?: number; residual_occurrence?: number; residual_detectability?: number; decision?: string;
}
export function useAddQra(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewQraInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/qra`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}
export function useDeleteQra(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (qraId) => apiFetch(`/api/v1/change-control/requests/${id}/qra/${qraId}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}

// Approvals routing + effectiveness review.
export const CC_APPROVAL_STAGES = ['DEPT_HEAD', 'QA_PRELIMINARY', 'COMMITTEE', 'QA_MANAGER', 'TECHNICAL_MANAGER', 'QUALITY_DIRECTOR', 'RA', 'THIRD_PARTY', 'CLOSURE'];
export const CC_APPROVAL_DECISIONS = ['PENDING', 'APPROVED', 'REJECTED', 'CONDITIONAL'];
export const CC_EFF_OUTCOMES = ['EFFECTIVE', 'PARTIALLY_EFFECTIVE', 'NOT_EFFECTIVE', 'NOT_REQUIRED'];
export interface NewApprovalInput { stage: string; decision?: string; approver_name?: string; approver_role?: string; comments?: string }
export interface NewEffInput { review_due_date?: string; outcome?: string; objective_achieved?: boolean | null; adverse_impact?: boolean | null; capa_ref?: string }

export function useAddApproval(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewApprovalInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/approvals`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}
export function useDeleteApproval(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (aid) => apiFetch(`/api/v1/change-control/requests/${id}/approvals/${aid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}
export function useAddEffectiveness(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewEffInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/effectiveness`, { method: 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}
export function useDeleteEffectiveness(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (eid) => apiFetch(`/api/v1/change-control/requests/${id}/effectiveness/${eid}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['change-control', 'request', id] }),
  });
}

// Part E — implementation actions (CC-7).
export const CC_ACTION_FSM: Record<string, string[]> = {
  OPEN: ['IN_PROGRESS', 'CANCELLED'], IN_PROGRESS: ['COMPLETED', 'CANCELLED'],
  COMPLETED: ['QA_VERIFIED', 'REOPENED'], QA_VERIFIED: ['REOPENED'],
  REOPENED: ['IN_PROGRESS', 'COMPLETED', 'CANCELLED'], CANCELLED: [],
};
export const CC_ACTION_CATEGORIES = ['VALIDATION', 'ENGINEERING', 'QC', 'PRODUCTION', 'PACKAGING', 'TRAINING', 'RA', 'DMS', 'EHS', 'OTHER'];
export interface NewActionInput { description: string; category?: string; owner_name?: string; target_date?: string; source_impact_id?: string }
function invalidateCr(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['change-control', 'request', id] });
}
export function useAddAction(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewActionInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/actions`, { method: 'POST', body }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useConvertActions(id: string) {
  const qc = useQueryClient();
  return useMutation<{ converted: number }, Error, void>({
    mutationFn: () => apiFetch(`/api/v1/change-control/requests/${id}/actions/convert`, { method: 'POST' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function usePatchAction(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, { actionId: string; patch: Record<string, unknown> }>({
    mutationFn: ({ actionId, patch }) => apiFetch(`/api/v1/change-control/requests/${id}/actions/${actionId}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useDeleteAction(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (actionId) => apiFetch(`/api/v1/change-control/requests/${id}/actions/${actionId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}

// Evidence repository (CC-8) — categorized uploads stored in MinIO (fs fallback).
// required_assessments on a change type (VALIDATION/REGULATORY/TRAINING) map 1:1
// to evidence categories, which drives the per-CR required-evidence checklist.
export const CC_EVIDENCE_CATEGORIES = ['VALIDATION', 'TRAINING', 'REGULATORY', 'DOCUMENT', 'TEST_RESULTS', 'COMMITTEE_MINUTES', 'OTHER'];
export interface NewEvidenceInput {
  category: string;
  title?: string;
  filename?: string;
  mime?: string;
  artifactBase64?: string;
  notes?: string;
  linked_action_id?: string;
}
export function useAddEvidence(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewEvidenceInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/evidence`, { method: 'POST', body }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useDeleteEvidence(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (evidenceId) => apiFetch(`/api/v1/change-control/requests/${id}/evidence/${evidenceId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}

// Sub-assessments (CC-11) — structured Regulatory / Validation / Training.
export const CC_SUB_KINDS = ['REGULATORY', 'VALIDATION', 'TRAINING'];
export const CC_SUB_STATUS = ['PENDING', 'IN_PROGRESS', 'SUBMITTED', 'SCHEDULED', 'APPROVED', 'COMPLETED', 'NOT_REQUIRED'];
export const CC_VALIDATION_TYPES = ['DQ', 'IQ', 'OQ', 'PQ', 'PV', 'CSV', 'CLEANING', 'HOLD_TIME', 'METHOD', 'OTHER'];
export const CC_TRAINING_TYPES = ['READ_UNDERSTAND', 'CLASSROOM', 'OJT', 'ASSESSMENT'];
// Closed-for-gate statuses (a sub-assessment satisfies a CC-9 closure gate here).
export const CC_SUB_CLOSED = new Set(['APPROVED', 'COMPLETED', 'NOT_REQUIRED']);
export interface NewSubAssessmentInput {
  kind: string; title: string; required?: boolean;
  variation_type?: string; country?: string; authority?: string; submission_before_impl?: boolean;
  validation_type?: string; protocol_ref?: string; report_ref?: string;
  audience?: string; training_type?: string; document_ref?: string;
  reference_no?: string; target_date?: string; owner_name?: string; notes?: string; create_action?: boolean;
}
export function useAddSubAssessment(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, NewSubAssessmentInput>({
    mutationFn: (body) => apiFetch(`/api/v1/change-control/requests/${id}/sub-assessments`, { method: 'POST', body }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useGenerateSubAssessments(id: string) {
  const qc = useQueryClient();
  return useMutation<{ created: number }, Error, void>({
    mutationFn: () => apiFetch(`/api/v1/change-control/requests/${id}/sub-assessments/generate`, { method: 'POST' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function usePatchSubAssessment(id: string) {
  const qc = useQueryClient();
  return useMutation<Record<string, unknown>, Error, { subId: string; patch: Record<string, unknown> }>({
    mutationFn: ({ subId, patch }) => apiFetch(`/api/v1/change-control/requests/${id}/sub-assessments/${subId}`, { method: 'PATCH', body: patch }),
    onSuccess: () => invalidateCr(qc, id),
  });
}
export function useDeleteSubAssessment(id: string) {
  const qc = useQueryClient();
  return useMutation<unknown, Error, string>({
    mutationFn: (subId) => apiFetch(`/api/v1/change-control/requests/${id}/sub-assessments/${subId}`, { method: 'DELETE' }),
    onSuccess: () => invalidateCr(qc, id),
  });
}

export const CC_STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Draft',
  SUBMITTED: 'Submitted',
  PENDING_QA_PRELIMINARY: 'QA preliminary',
  ACCEPTED_CR_NUMBER_ISSUED: 'CR issued',
  IMPACT_ASSESSMENT: 'Impact assessment',
  RISK_ASSESSMENT: 'Risk assessment',
  COMMITTEE_REVIEW: 'Committee review',
  PENDING_FINAL_APPROVAL: 'Pending approval',
  APPROVED_FOR_IMPLEMENTATION: 'Approved',
  IMPLEMENTATION_IN_PROGRESS: 'Implementing',
  PENDING_EFFECTIVENESS: 'Effectiveness',
  PENDING_CLOSURE: 'Pending closure',
  CLOSED_APPROVED: 'Closed',
  REJECTED: 'Rejected',
  CANCELLED: 'Cancelled',
  ON_HOLD: 'On hold',
};
export const CC_LEVEL_LABEL: Record<string, string> = { LEVEL_1: 'Minor', LEVEL_2: 'Major', LEVEL_3: 'Critical' };
